import * as angular from 'angular';
import * as _ from 'lodash';
import * as d3 from 'd3';

import { ComponentDefinition, SelectorType, Group, KpiConfiguration, IValueViewModel, ISortConfiguration, ObjectType } from '../../../models';
import { IBusinessKpiService, IGroupSelectionService } from '../../../services';
import { wtDroppableDrop, seriesAdded } from '../../../common';
import { BusinessKpiWidget } from '../businessKpi.widget';

interface KpiSerie {
    isBindable: boolean;
    kpi: KpiConfiguration;
    name: string;
    serieName: string;
    viewModel: IValueViewModel;
}

class BusinessKpiSeriesController implements angular.IComponentController {
    groups: Group[];
    isBindable: boolean;
    item: BusinessKpiWidget;
    selectedKpi: KpiConfiguration;
    series: KpiSerie[];
    sortConfig: ISortConfiguration;
    viewModels: IValueViewModel[];

    static $inject = [
        '$scope',
        'businessKpiService',
        'groupSelectionService',
    ];

    constructor(
        private _$scope: angular.IScope,
        private _businessKpiService: IBusinessKpiService,
        private _groupSelectionService: IGroupSelectionService,
    ) {
        this.sortConfig = {
            animation: 150,
            onSort: evt => this.sort(evt),
            handle: '.drag-handle',
        };
    }

    $onInit() {
        this._$scope.$watch(() => this.item, (newValue) => {
            if (newValue) {
                this._$scope.$watchCollection(() => this.item.configuration.kpis, () => {
                    this.refresh();
                });
                this._$scope.$watch(() => this.item.configuration.nameConfiguration, () => {
                    this.refresh();
                }, true);
            }
        });

        this._$scope.$on(wtDroppableDrop, (event, data) => {
            if (data.itemId) {
                const guid = data.itemId.replace('id', '');
                if (data.binId) {
                    const bin = data.binId.split('+');
                    this.updateSerie(bin[0], bin[1], guid);
                } else {
                    this.addSeries(guid);
                }
            }
        });

        this._$scope.$on(seriesAdded, (event, data) => {
            this.addSeries(data.guid);
        });
    }

    refresh() {
        this._businessKpiService
            .getViewModels(this.item.configuration.kpis, this.item.configuration.nameConfiguration)
            .then(vm => {
                this.series = this.item.configuration.kpis
                    .map(d => {
                        const found = _.find(vm, v => v.kpi.identifier === d.value + d.guid);
                        return {
                            kpi: d,
                            name: found?.kpi?.name ?? '',
                            serieName: (found?.kpi as any)?.serieName ?? '',
                            viewModel: found?.viewModel ?? null,
                            isBindable: found?.viewModel?.objectType === ObjectType.Identifier ?? false
                        };
                    });
            })
            .finally(() => this.recomputeRelativePath());
    }

    deleteSeries(kpi: any) {
        _.remove(this.item.configuration.kpis, k => kpi.guid === k.guid && kpi.value === k.value);
    }

    select(kpi: KpiSerie) {
        this.selectedKpi = kpi.kpi;
    }

    private recomputeRelativePath() {
        const selected = this._groupSelectionService.selectedGroup();
        if (!selected) {
            return;
        }
        const toCompute = this.item.configuration.kpis.filter(s =>
            s.isBoundToGroup
            //&& !s.relativePath
            && s.kpiType === SelectorType.identifier
        );

        if (toCompute.length > 0) {
            const guids = toCompute.map(s => s.guid);
            const vms = this.viewModels.filter(vm => guids.indexOf(vm.guid) >= 0);
            this.remapPaths(selected, toCompute, vms, this.groups);
        }
    }

    private remapPaths(
        selected: Group,
        toCompute: KpiConfiguration[],
        data: IValueViewModel[],
        groups: Group[]
    ) {
        const typeReference = selected.type;
        const tuple = toCompute.map(d => {
            return {
                kpi: d,
                vm: _.find(data, v => v.guid === d.guid)
            };
        });
        const byAbsolutePath = d3.nest<any>()
            .key(d => d.vm.groupAbsolutePath)
            .entries(tuple);
        byAbsolutePath.forEach(d => {
            const startName = d.key;

            let current = _.find(groups, g => g.absoluteName === startName);
            while (current && current.type !== typeReference) {
                current = _.find(groups, g => g.guid === current.parentGUID);
            }

            const newName = current
                ? startName.replace(current.absoluteName, '.')
                : null;
            d.values.forEach((v: any) => v.kpi.relativePath = newName);
        });
    }

    private sort(evt: any) {
        evt.models.forEach((s, i) => s.kpi.sortOrder = i);
        this.item.configuration.kpis = evt.models.map((d: any) => d.kpi);
    }

    private addSeries(guid: string): void {
        const selectedGroup = this._groupSelectionService.selectedGroup();
        const canBind = this.isBindable
            && selectedGroup
            && _.some(selectedGroup.identifierCollection, i => i.guid === guid);
        this._businessKpiService
            .getSeries(guid, canBind)
            .then(config => this.item.configuration.kpis.push(config))
            .catch(reason => console.log(`Unable to add serie : ${reason}`));
    }

    private updateSerie(oldValue: string, oldGuid: string, newGuid: string) {
        const old: KpiConfiguration = _.find(this.item.configuration.kpis, s => s.guid === oldGuid && s.value === oldValue);
        const vm = _.find(this.viewModels, v => v.guid === newGuid);
        const isIdentifier = vm?.objectType === ObjectType.Identifier;
        if (old) {
            old.guid = newGuid;
            old.originalGuid = newGuid;
            old.kpiType = vm.objectType as unknown as SelectorType;
            old.identifierType = vm.identifierType;
            old.isBoundToGroup = isIdentifier && old.isBoundToGroup;
            old.hasReconciledValue = vm.hasReconciledValue;
            old.isConstraint = vm.isConstraint;
            this.item.configuration.kpis = angular.copy(this.item.configuration.kpis);
        }
    }
}

export const wtBusinessKpiSeries: ComponentDefinition = {
    componentId: 'wtBusinessKpiSeries',
    controller: BusinessKpiSeriesController,
    template: require('./businessKpiSeries.component.html'),
    controllerAs: 'vm',
    bindings: {
        groups: '<',
        item: '=',
        isBindable: '=',
        viewModels: '<'
    }
};